// Pure input-hardening model for the global "Search iYuga" results dropdown
// (EW-1181 / NEMESIS-nemz19-search). Two defects, one model:
//   1. A very long query (the nemesis pasted 10,000 chars) was sent verbatim as
//      GET /api/v2/search-users/?search=<10k>… and died at the CDN/LB URL-length
//      limit (net::ERR_FAILED) — Explore search defends against this (EW-604) but
//      global search did not.  `clampSearchQuery` caps the query length.
//   2. When ANY search request fails, the app only console.error'd — the dropdown
//      kept whatever stale rows the previous query left, with zero user feedback.
//      `shouldShowSearchError` / `shouldShowEmptyState` drive a graceful error
//      message that also takes precedence over the "No … Found" empty state.
//
// Kept out of the component so the length + message-precedence logic is
// unit-testable rather than only live-QA-able. The component owns the side
// effects (setState, the maxLength attribute); this decides ONLY the values.

// Hard cap on the global-search query length. A username/wheel-name search is
// realistically well under this; the point is to keep the request URL far below
// the ~8KB CDN/LB limit so it can never fail the way the 10k paste did.
export const SEARCH_MAX_QUERY_LEN = 200;

// Truncate an over-long query to the cap. Non-strings collapse to "" (defensive
// — the input always hands us a string, but a programmatic setter might not).
export function clampSearchQuery(value, max = SEARCH_MAX_QUERY_LEN) {
  if (typeof value !== "string") return "";
  const cap =
    Number.isInteger(max) && max > 0 ? max : SEARCH_MAX_QUERY_LEN;
  return value.length > cap ? value.slice(0, cap) : value;
}

// Show the "search failed" message when hardening is on, a fetch errored, and we
// are not mid-request (a retry in flight shows the loader, not the error).
export function shouldShowSearchError({ hardeningOn, fetchError, loading }) {
  return Boolean(hardeningOn && fetchError && !loading);
}

// ISP-9999 (kelly-u0b5jcr55ht-...-web-dev-getting-error-message): the shared
// BE throttle on search-users/search-wheels (SearchAnonBurstThrottle, 30/min
// per account despite the "Anon" name — home/api/v1/throttling.py) is keyed
// per authenticated user, not per browser session, so it trips from entirely
// normal use (retyping a query a few times, switching the Users/Wheels tab,
// scrolling for more results — each fires its own page-1/page-N request) and
// is shared with every other tab/session signed into the same account. A 429
// from that throttle got the exact same "Search failed. Please check your
// connection and try again." text as a real network failure, which is wrong:
// there is nothing wrong with the connection, and telling the user to wait
// is more accurate and actionable than telling them to check their network.
export function getSearchErrorMessage(status) {
  if (status === 429) {
    return "You're searching a little too fast. Please wait a moment and try again.";
  }
  return "Search failed. Please check your connection and try again.";
}

// The original empty-state gate was `!loading && searchText && listEmpty`. When
// hardening is on AND a fetch error is showing, suppress the misleading
// "No … Found" (the results are empty because the request FAILED, not because
// nothing matched). With hardening OFF this returns the original value exactly
// (byte-identical), because `hardeningOn && fetchError` is false.
export function shouldShowEmptyState({
  hardeningOn,
  fetchError,
  loading,
  searchText,
  listEmpty,
}) {
  const base = Boolean(!loading && searchText && listEmpty);
  if (!base) return false;
  return !(hardeningOn && fetchError);
}
